/**
 * Created by rathompson on 5/13/22
 * File: CSPDeviceTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  ConnectedTable,
  ConnectedTableLayout,
  TableProvider,
} from '@panwds/react-table'
import { noop } from '@panwds/react-ui'
import styled from 'styled-components'
import {
  COLUMN_ASSET_TYPE,
  COLUMN_MODEL,
  COLUMN_NAME,
  COLUMN_SN,
} from '../../constants/LabelConstants'
import FiltersBar from '../common/table/FiltersBar'
import { isEqual } from 'lodash'
import V5TableWrapper from '../../../common/components/table/V5TableWrapper'
import NewCellRenderer from '../common/table/cellRenderer/NewCellRenderer'
import NewDeviceAssetTypeRenderer from '../common/table/cellRenderer/NewDeviceAssetTypeRenderer'

const CHECK_BOX_CSS = styled.div`
  input[type=checkbox] {
    &[disabled] {
      background-image: none;
      border-width: 1px;
      border-style: solid;
      border-radius: 0.063rem;
      --tw-border-opacity: 1;
      border-color: rgba(218,219,219,var(--tw-border-opacity));
      background-color: transparent;
    }
  }
`

const CSPDeviceTable = ({ data, header, loading, setSelected: _setSelected }) => {

  const [selected, setSelected] = useState([])
  const tableRef = useCallback((node) => {
    if (node) {
      if (!isEqual(node.selectedFlatRows, selected)) {
        setSelected(node.selectedFlatRows)
      }
    }
  }, [selected])

  useEffect(() => {
    _setSelected(selected)
  }, [_setSelected, selected])

  const tableContainerRef = useRef()

  /**
   app_id: "panorama"
   asset_type: "prod"
   device_name: ""
   model: "Panorama"
   serial_number: "cyrusscale074"
   sku: "PAN-PRA-100"
   */
  const columns = useMemo(() => {
    return [
      {
        accessor: 'serial_number',
        Header: COLUMN_SN,
        width: 200,
        id: 'serial_number',
        Cell: NewCellRenderer,
      },
      {
        accessor: 'device_name',
        Header: COLUMN_NAME,
        width: 200,
        id: 'device_name',
        Cell: NewCellRenderer
      },
      {
        accessor: 'model',
        Header: COLUMN_MODEL,
        width: 200,
        id: 'model',
        Cell: NewCellRenderer
      },
      {
        accessor: 'asset_type',
        Header: COLUMN_ASSET_TYPE,
        width: 200,
        id: 'asset_type',
        Cell: NewDeviceAssetTypeRenderer,
        cellToString: ({ row }) => row?.original?.asset_type,
      },
    ]
  }, [])

  const updateCheckboxes = useCallback((rows) => {
    rows.forEach((data, index) => {
      const tableContainer = tableContainerRef.current
      if (!tableContainer) {
        return rows
      }
      const rowElement = tableContainer.querySelector(`[aria-rowindex="${index}"]`)
      if (rowElement) {
        const disabled = data?.disabled
        const checkbox = rowElement.querySelector('input[type=checkbox]')
        if (checkbox) {
          checkbox.disabled = disabled
          checkbox.readOnly = disabled
          checkbox.style.cursor = 'pointer'
          if (disabled) {
            checkbox.style.cursor = 'not-allowed'
            checkbox.checked = false
          }
        }
      }
    })
    return rows
  }, [])

  return (
    <CHECK_BOX_CSS>
      <V5TableWrapper ref={tableContainerRef} className='tw-absolute tw-inset-0 tw-flex tw-flex-col tw-flex-1'>
        <TableProvider
          tableInstanceRef={tableRef}
          columns={columns}
          data={data}
          enablePagination
          enableRowSelect
          enableResizeColumns
          enableColumnOrder
          enableColumnSort
          isLoading={loading}
          onSelectedRowsChange={() => updateCheckboxes(data)}
          onPaginationChange={() => updateCheckboxes(data)} // APPORTAL-7808 - on page to refresh disable checkbox again
        >
          {Boolean(header) && header}
          <FiltersBar/>
          <ConnectedTableLayout
            overflow
          >
            <ConnectedTable/>
          </ConnectedTableLayout>
        </TableProvider>
      </V5TableWrapper>
    </CHECK_BOX_CSS>
  )
}

CSPDeviceTable.propTypes = {
  header: PropTypes.element,
  data: PropTypes.array.isRequired
}

CSPDeviceTable.defaultProps = {
  data: [],
  setSelected: noop
}
export default CSPDeviceTable

